import type { ReplaceOperation } from 'fast-json-patch/commonjs/core';

import { cloneDeep } from '@disy/lib/clone-deep';
import { assertNonNullable } from '@disy/lib/custom-error';
import { Observable } from '@disy/lib/event-util';
import { isEqual } from '@disy/lib/is-equal';
import type { Settings } from '@disy/workbook.lib/settings';

import type { ChangeVisualizationCommandPayload } from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import { isValidMainObjecttype } from 'cadenza/repository/model/objecttype-id.gen';
import type { SortType } from 'cadenza/repository/model/sort-type.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { ATTRIBUTE_BUNDLE_LIMITS_SETTING_KEYS_TO_BUNDLE_NAME_MAPPING } from 'cadenza/workbook/advanced-data-settings-dialog/advanced-data-settings-dialog-utils';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import { getViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { editActiveAttribute } from 'cadenza/workbook/attribute-modification-dialog/edit-attribute';
import type { AttributeHighlightingRule } from 'cadenza/workbook/attribute-modification-dialog/presentation/attribute-highlighting/attribute-highlighting-types';
import {
  checkHighlightingRules,
  checkHighlightingRulesForMultipleAttributes,
  showModalToConfirmAttributeRemoval,
} from 'cadenza/workbook/attribute-modification-dialog/presentation/attribute-highlighting/remove-attribute-dialog-check-highlighting-rules';
import {
  type AttributeBundleDefinitionLimitsSpecifics,
  getBundleAttributeCountAndLimits,
  hasValidBundlesConfig,
  modifyDataViewAttribute,
} from 'cadenza/workbook/data-view/data-view-utils';
import { getBaseMapViewConfigOrSelectInDialog } from 'cadenza/workbook/default-map-utils/default-map-utils';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import { UNUSED_BUNDLE_NAME } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { VisualizationVariant } from 'cadenza/workbook/model/visualization-variant.gen';
import {
  ToAnyReachableSetsCache,
  isAttributeAcceptedForDataView,
} from 'cadenza/workbook/state/workbook-reachability.state';
import { getAttributePresentation } from 'cadenza/workbook/tables/presentation';
import {
  SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY,
  createSynchronizedAttributeModificationCommandsFromUserInput,
} from 'cadenza/workbook/view/chart/combination-chart-utils';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getAttributeById,
  getAttributesByBundle,
  getConditionById,
  getDataViewById,
  getObjecttypeById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { createDataViewAttributeModificationCommand } from 'cadenza/workbook/workbook-panel/settings/attribute-modification-utils';
import { createDefaultClassesCommands } from 'cadenza/workbook/workbook-panel/settings/map/settings/classes-setting/combined-classification-utils';

export class DataView extends Observable {
  readonly #dataViewId: DataViewId;
  readonly #viewId: ViewId;
  #previousSettings: Settings = {};

  constructor(dataViewId: DataViewId, viewId: ViewId) {
    super();
    this.#dataViewId = dataViewId;
    this.#viewId = viewId;
    observeStore(
      getStore(),
      (state) => getDataViewById(state, this.dataViewId),
      (dataViewDto) => this.#updateFromStore(dataViewDto),
    );
  }

  get viewId() {
    return this.#viewId;
  }

  get dataViewId() {
    return this.#dataViewId;
  }

  get visualizationKey() {
    return this._dataView.visualizationKey;
  }

  get visualizationName() {
    return this._dataView.visualizationName;
  }

  async changeVisualization(visualizationKey: DataViewVisualizationKey, visualizationVariant: VisualizationVariant) {
    // Only issue command when anything changed.
    if (this.visualizationKey === visualizationKey && !visualizationVariant) {
      return Promise.resolve();
    }
    const commandPayload: ChangeVisualizationCommandPayload = {
      viewId: this.#viewId,
      dataViewId: this.#dataViewId,
      visualizationKey,
      visualizationVariant,
    };

    if (this.#changingVisualizationToMap(visualizationKey)) {
      const { baseMapViewId, canceled } = await getBaseMapViewConfigOrSelectInDialog();
      if (canceled) {
        return;
      }
      commandPayload.baseMapViewId = baseMapViewId;
    }
    const changeVisualizationCommand: Command[] = [Command.CHANGE_VISUALIZATION(commandPayload)];
    const commands = changeVisualizationCommand.concat(
      createRemoveClassificationFromIndicatorCommands(this.#dataViewId, visualizationKey, this.getAllAttributes()),
    );
    return getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
  }

  #changingVisualizationToMap(newVisualizationKey: DataViewVisualizationKey) {
    return getViewType(this.visualizationKey) !== 'MAP' && getViewType(newVisualizationKey) === 'MAP';
  }

  /**
   * Returns all Attributes contained by the specified bundle.
   *
   * We fail hard when requesting a non-existing bundle because from the use-case we should always
   * have the knowledge about what bundles can be requested. Failing here helps to find problems
   * with the bundle structure early (e.g. when GUI works on old model state with wrong bundles).
   * If testing for dynamic bundles is required use {@link hasAttributeBundle} or
   * {@link getAllAttributeBundles} instead.
   *
   * @param bundleName - Name of the bundle to get attributes from
   * @return The attributes of the specified bundle
   * @throws {Error} If the bundle does not exist in the data view
   */
  getAttributes(bundleName: AttributeBundleName) {
    const attributes = this.getAllAttributeBundles()[bundleName];
    if (!attributes) {
      throw Error(`Requested bundle '${bundleName}' which does not exist in this data view`);
    }
    return attributes;
  }

  /**
   * Returns all Attributes from all bundles combined in one Array.
   *
   * @return The attributes of all bundles
   */
  getAllAttributes() {
    return Object.values(this.getAllAttributeBundles()).flat();
  }

  getBundleOfAttribute(activeAttributeId: ActiveAttributeId) {
    for (const [attributeBundleName, bundleContent] of Object.entries(this.getAllAttributeBundles())) {
      if (bundleContent.some((attribute) => attribute.id === activeAttributeId)) {
        return attributeBundleName as AttributeBundleName;
      }
    }
    return null;
  }

  getAttribute(attributeId: ActiveAttributeId) {
    return this.getAllAttributes().find((attribute) => attribute.id === attributeId);
  }

  /**
   * Returns all Attributes from all bundles grouped by bundle name.
   *
   * @return The attributes of all bundles.
   */
  getAllAttributeBundles() {
    return getAttributesByBundle(getStore().getState(), this.#dataViewId);
  }

  hasAttributeBundle(attributeBundleName: AttributeBundleName) {
    return this.getAllAttributeBundles()[attributeBundleName] !== undefined;
  }

  getAllAttributesIgnoringUnusedBundle() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { UNUSED, ...bundles } = this.getAllAttributeBundles();
    return Object.values(bundles).flat();
  }

  /**
   * Reorders the attributes in the specified bundle.
   *
   * @param attributeBundleName - The bundle to reorder
   * @param attributeIds - The attribute ids in the new order
   * @param settings - The DataView settings to set along
   */
  reorderAttributes(attributeBundleName: AttributeBundleName, attributeIds: ActiveAttributeId[], settings?: Settings) {
    // create a map of attribute ids per bundle
    const attributeIdsByBundle = { ...this._dataView.attributes };
    // override the changed one
    attributeIdsByBundle[attributeBundleName] = attributeIds;

    getStore().dispatch(
      executeCommand(
        this._appendSettingsCommandOptionally(
          Command.REORDER_ATTRIBUTES({
            dataViewId: this.#dataViewId,
            attributeIdsByBundle,
          }),
          settings,
        ),
      ),
    );
  }

  /**
   * Adds an attribute to the DataView into the specified bundle.
   * If the attribute with the given AttributeID is already present in another bundle of the
   * DataView its moved while preserving all attribute modifications and settings.
   *
   * @param attributeId - The ID of the attribute to add
   * @param attributeBundleName - The bundle to add the attribute to
   * @param index - The index at which the attribute should get added
   * @param settings - The DataView settings to set along
   */
  async addAttribute(
    attributeId: AttributeId | ActiveAttributeId,
    attributeBundleName?: AttributeBundleName,
    index?: number,
    settings?: Settings,
  ) {
    let command: Command = this._addAttributeCommand(attributeId, attributeBundleName, index);
    if (attributeBundleName === 'COMBINED_CLASSIFICATION') {
      command = await createDefaultClassesCombinedClassificationCommands(this.#dataViewId, command, attributeId);
    }
    const result = await getStore()
      .dispatch(executeCommand(this._appendSettingsCommandOptionally(command, settings)))
      .unwrap();
    assertNonNullable(result);
    const operation = result.operations.find((op) => op.op === 'replace' && op.path.startsWith('/activeAttributeById'));
    const activeAttributeId = (operation as ReplaceOperation<{ id: ActiveAttributeId }>).value.id;
    observeStore(
      getStore(),
      (state) => getAttributeById(state, activeAttributeId),
      (attribute) => {
        if (attribute) {
          if (attribute.status === 'INCOMPATIBLE_AGGREGATION_FUNCTION') {
            editActiveAttribute(activeAttributeId, this.dataViewId);
          }
          return 'unsubscribe';
        }
      },
    );
    return activeAttributeId;
  }

  _addAttributeCommand(
    attributeId: AttributeId | ActiveAttributeId,
    attributeBundleName?: AttributeBundleName,
    index?: number,
  ) {
    return Command.ADD_ATTRIBUTE({
      viewId: this.#viewId,
      dataViewId: this.#dataViewId,
      attributeBundleName,
      attributeId,
      index,
    });
  }

  /**
   * Removes an attribute from the DataView.
   *
   * @param attributeId - The ID of the attribute to remove
   * @param settings - The DataView settings to set along
   */
  removeAttribute(attributeId: ActiveAttributeId, settings?: Settings) {
    // Check if there are highlighting rules defined on other attributes that depend on the
    // attribute to be removed and ask the user for confirmation if there are any.
    const { dependantHighlightingRuleNames, commandsToResetRules } = checkHighlightingRules(
      attributeId,
      this.getAllAttributes(),
      this.#dataViewId,
    );

    if (dependantHighlightingRuleNames.length) {
      const removeAttributesAndHighlightingRulesOnceUserConfirmsDialog = () => {
        commandsToResetRules.push(this._createRemoveAttributeCommandWithOptionalSettings(attributeId, settings));
        getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands: commandsToResetRules })));
      };
      showModalToConfirmAttributeRemoval(
        dependantHighlightingRuleNames,
        removeAttributesAndHighlightingRulesOnceUserConfirmsDialog,
      );
    } else {
      getStore().dispatch(
        executeCommand(this._createRemoveAttributeCommandWithOptionalSettings(attributeId, settings)),
      );
    }
  }

  replaceAttribute(
    removedAttributeId: ActiveAttributeId,
    addedAttributeId: AttributeId | ActiveAttributeId,
    attributeBundleName: AttributeBundleName,
    index: number,
    settings?: Settings,
  ) {
    const commands: Command[] = [];

    if (this.attributeBundleDefinitions[UNUSED_BUNDLE_NAME]) {
      commands.push(this._createAddAttributeCommandWithOptionalSettings(removedAttributeId, UNUSED_BUNDLE_NAME));
    } else {
      commands.push(this._createRemoveAttributeCommandWithOptionalSettings(removedAttributeId, settings));
    }

    const createAddCommandAndExecuteComposite = () => {
      commands.push(
        this._createAddAttributeCommandWithOptionalSettings(addedAttributeId, attributeBundleName, index, settings),
      );
      getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
    };

    // Check if there are highlighting rules defined on other attributes that depend on the
    // attribute to be removed and ask the user for confirmation if there are any.
    const { dependantHighlightingRuleNames, commandsToResetRules } = checkHighlightingRules(
      removedAttributeId,
      this.getAllAttributes(),
      this.#dataViewId,
    );

    if (dependantHighlightingRuleNames.length) {
      showModalToConfirmAttributeRemoval(dependantHighlightingRuleNames, () => {
        commands.push(...commandsToResetRules);
        createAddCommandAndExecuteComposite();
      });
    } else {
      createAddCommandAndExecuteComposite();
    }
  }

  /**
   * Removes attributes from the DataView.
   *
   * @param attributeIds - The list of attribute IDs to remove
   */
  removeAttributes(attributeIds: ActiveAttributeId[]) {
    const commandsToRemoveAttributesOnly = attributeIds.map((attributeId) =>
      this._createRemoveAttributeCommandWithOptionalSettings(attributeId),
    );

    const { dependantHighlightingRuleNames, commandsToResetRules } = checkHighlightingRulesForMultipleAttributes(
      attributeIds,
      this.getAllAttributes(),
      this.#dataViewId,
    );
    if (dependantHighlightingRuleNames.length) {
      const removeAttributesAndHighlightingRulesOnceUserConfirmsDialog = () => {
        getStore().dispatch(
          executeCommand(
            Command.COMPOSITE_COMMAND({ commands: commandsToResetRules.concat(commandsToRemoveAttributesOnly) }),
          ),
        );
      };
      showModalToConfirmAttributeRemoval(
        dependantHighlightingRuleNames,
        removeAttributesAndHighlightingRulesOnceUserConfirmsDialog,
      );
    } else {
      getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands: commandsToRemoveAttributesOnly })));
    }
  }

  get name() {
    return this._dataView.name;
  }

  get defaultName() {
    return this._dataView.defaultName;
  }

  _appendSettingsCommandOptionally(command: Command, settings?: Settings) {
    if (!settings) {
      return command;
    }
    return command.append(
      Command.CHANGE_DATA_VIEW_SETTINGS({
        dataViewId: this.#dataViewId,
        settings,
      }),
    );
  }

  sortByAttribute(attributeId: ActiveAttributeId, order: SortType, hierarchyLevel?: string) {
    const dataViewId = this.#dataViewId;
    return getStore().dispatch(
      executeCommand(
        Command.SORT_DATA_VIEW_BY_ATTRIBUTE({
          dataViewId,
          attributeId,
          order,
          hierarchyLevel,
        }),
      ),
    );
  }

  resetAttribute(attributeId: ActiveAttributeId) {
    const dataViewId = this.#dataViewId;
    return getStore().dispatch(
      executeCommand(
        Command.RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
          dataViewId,
          attributeId,
        }),
        // also synchronize if necessary, relevant only for combination chart
        ...createSynchronizedAttributeModificationCommandsFromUserInput(
          getAttributeById(getStore().getState(), attributeId),
          // SORT_ORDER is not set from the dialog, so ignore it
          SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY.filter((key) => key !== 'SORT_ORDER').map((key) => [key, null]),
          dataViewId,
        ),
      ),
    );
  }

  modifyAttribute(
    attributeId: ActiveAttributeId,
    key: AttributeModificationKey,
    value: string | null,
  ): Promise<unknown>;
  modifyAttribute(
    attributeId: ActiveAttributeId,
    modification: [key: AttributeModificationKey, value: string | null][],
  ): Promise<unknown>;

  modifyAttribute(
    attributeId: ActiveAttributeId,
    modification: AttributeModificationKey | [AttributeModificationKey, string | null][],
    value: string | null = null,
  ) {
    return modifyDataViewAttribute(this.#dataViewId, attributeId, modification, value);
  }

  _setAttributeModification(
    attributeId: ActiveAttributeId,
    modificationType: AttributeModificationKey,
    value: string | null,
  ): Command {
    const dataViewId = this.#dataViewId;
    return value === null ?
        Command.REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION({
          dataViewId,
          attributeId,
          modificationType,
        })
      : Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION({
          dataViewId,
          attributeId,
          modificationType,
          value,
        });
  }

  modifyAttributeAndRules(
    attributeId: ActiveAttributeId,
    modifications: [AttributeModificationKey, string | null][],
    rules: AttributeHighlightingRule[],
  ) {
    const dataViewId = this.#dataViewId;
    const highlightingRules =
      rules ?
        [
          Command.SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES({
            dataViewId,
            attributeId,
            attributeHighlightingRules: rules,
          }),
        ]
      : [];

    return getStore().dispatch(
      executeCommand(
        Command.COMPOSITE_COMMAND({
          commands: modifications
            .map(([modification, value]) =>
              createDataViewAttributeModificationCommand(this.#dataViewId, attributeId, modification, value),
            )
            .concat(highlightingRules),
        }),
      ),
    );
  }

  /**
   * Updates the data view to the new data state if there are changes present,
   * indicated by more than `id` being present in the object.
   *
   * Only model changes are allowed. Don't update the view in this method call.
   */
  #updateFromStore(dataViewDto: DataViewDto) {
    // dataViewDto will be undefined where it is removed
    if (!dataViewDto) {
      return;
    }
    // Cloning, because the properties (at least the settings) of dataView is mutated
    const dataView = structuredClone(dataViewDto);
    // Settings are passed around by reference, which means we must copy them explicitly to make sure
    // we can actually detect any change.
    this.#previousSettings = { ...dataView.settings };
  }

  get mainObjecttype() {
    const mainObjecttype = this._dataView.mainObjecttype;
    return !isValidMainObjecttype(mainObjecttype.objecttypeId) ? null : mainObjecttype;
  }

  get settings() {
    return this._dataView.settings;
  }

  _enforceBundleLimitsCommands(changed: Settings) {
    const enforceLimitsCommands: Command[] = [];
    Object.keys(changed)
      .filter((key) => key !== 'undefined')
      .map((key) => {
        if (ATTRIBUTE_BUNDLE_LIMITS_SETTING_KEYS_TO_BUNDLE_NAME_MAPPING[key] !== undefined) {
          const maxValue = Number(changed[key]);
          const bundleName = ATTRIBUTE_BUNDLE_LIMITS_SETTING_KEYS_TO_BUNDLE_NAME_MAPPING[key];
          const bundleAttributes = this.getAllAttributeBundles()[bundleName];
          if (bundleAttributes) {
            const bundleAttributeCount = bundleAttributes.length;
            for (let i = bundleAttributeCount - 1; i >= maxValue; i--) {
              enforceLimitsCommands.push(this._addAttributeCommand(bundleAttributes[i].id, UNUSED_BUNDLE_NAME));
            }
          }
        }
      });
    return enforceLimitsCommands;
  }

  createUpdateDataViewSettingsCommands(newSettings: Settings) {
    const settings = { ...this.settings, ...newSettings };
    const changed = this.#getChangedSettings(settings);
    return this.#createUpdateDataViewSettingsCommands(changed);
  }

  /** @deprecated Use {@link setDataViewSetting} for updating individual settings instead. Write new function for multiple. */
  updateSettings(newSettings: Settings) {
    const settings = { ...this.settings, ...newSettings };
    const changed = this.#getChangedSettings(settings);
    if (Object.keys(changed).length > 0) {
      this.#previousSettings = settings;
    }
    const commands = this.#createUpdateDataViewSettingsCommands(changed);
    if (commands.length) {
      return getStore()
        .dispatch(executeCommand(...commands))
        .unwrap();
    }
  }

  #getChangedSettings(settings: { [p: string]: unknown }) {
    const changed: Settings = {};
    Object.keys(settings)
      .filter((key) => !isEqual(settings[key], this.#previousSettings[key]))
      .forEach((key) => {
        changed[key] = settings[key];
      });
    return changed;
  }

  #createUpdateDataViewSettingsCommands(changed: Settings) {
    const commands = [];
    if (Object.keys(changed).length > 0) {
      const enforceLimitsCommands = this._enforceBundleLimitsCommands(changed);
      commands.push(
        Command.CHANGE_DATA_VIEW_SETTINGS({
          dataViewId: this.#dataViewId,
          settings: changed,
        }),
      );
      commands.push(...enforceLimitsCommands);
    }
    return commands;
  }

  get conditions() {
    return this._dataView.conditions.map(getConditionClone);
  }

  /**
   * @return The attribute bundle definitions of the DataView
   */
  get attributeBundleDefinitions() {
    return this._dataView.attributeBundleDefinitions;
  }

  get attributeBundleDefinitionLimitsSpecifics() {
    return this._dataView.attributeBundleDefinitionLimitsSpecifics as AttributeBundleDefinitionLimitsSpecifics;
  }

  /**
   * Checks if the given WorkbookAttributeDto is reachable by this DataView.
   *
   * @param attribute - The WorkbookAttributeDto to check reachability of
   * @return True if reachable
   */
  isAttributeAccepted(attribute: WorkbookAttributeDto) {
    if (!this.mainObjecttype) {
      return true;
    }

    const mainObjecttypeId = this.mainObjecttype!.objecttypeId;
    const mainObjecttype = getObjecttypeById(getStore().getState(), mainObjecttypeId);
    if (!mainObjecttype) {
      return true;
    }

    const toAnyReachableCache = new ToAnyReachableSetsCache(getStore().getState());
    const basicMainObjecttypeId = mainObjecttype.underlyingBasicMainObjecttypeId;
    return isAttributeAcceptedForDataView(attribute, basicMainObjecttypeId, toAnyReachableCache);
  }

  /**
   * Checks whether the given attribute is a hierarchy and already is added on any bundle of the
   * data view.
   * Since data view is unaware of attribute bundle controllers this checks whether attribute can
   * be dropped to this data view.
   *
   * @param attribute - The attribute to check
   * @return True if hierarchy can be added, else false
   */
  acceptsAttribute({ id, hierarchyType }: WorkbookAttributeDto) {
    if (hierarchyType) {
      return !this.getAllAttributes().some((attribute) => attribute.baseId === id);
    }
    return true; // we accept non hierarchies
  }

  _createAddAttributeCommandWithOptionalSettings(
    attributeId: AttributeId | ActiveAttributeId,
    attributeBundleName: AttributeBundleName,
    index?: number,
    settings?: Settings,
  ) {
    return this._appendSettingsCommandOptionally(
      Command.ADD_ATTRIBUTE({
        viewId: this.#viewId,
        dataViewId: this.#dataViewId,
        attributeBundleName,
        attributeId,
        index,
      }),
      settings,
    );
  }

  // `settings` param is optional
  _createRemoveAttributeCommandWithOptionalSettings(attributeId: ActiveAttributeId, settings?: Settings) {
    return this._appendSettingsCommandOptionally(
      Command.REMOVE_ATTRIBUTE({
        dataViewId: this.#dataViewId,
        attributeId,
      }),
      settings,
    );
  }

  /**
   * Gets the attribute count and limits for all bundles of the data view
   *
   * @return property names: attribute bundle names;
   *   values: attribute count and limits for the bundle
   */
  getAttributeCountAndLimitsPerBundle() {
    return getBundleAttributeCountAndLimits(this.attributeBundleDefinitions, this.getAllAttributeBundles());
  }

  hasValidBundlesConfig() {
    return hasValidBundlesConfig(this.attributeBundleDefinitions, this.getAllAttributeBundles());
  }

  get defaultSpatialGeometryAttributeId() {
    return this._dataView.defaultSpatialGeometryAttributeId;
  }

  get _dataView() {
    return getDataViewById(getStore().getState(), this.dataViewId);
  }
}

// Attribute presentation 'CLASSIFICATION' is not supported by indicator view, therefore it is removed when switching to indicator.
function createRemoveClassificationFromIndicatorCommands(
  dataViewId: DataViewId,
  visualizationKey: DataViewVisualizationKey,
  attributes: WorkbookAttributeDto[],
) {
  const measuresWithClassification = attributes.filter(
    (attr) => getAttributePresentation(attr) === 'CLASSIFICATION' && attr.category === 'MEASURE',
  );
  return visualizationKey === 'OTHER.INDICATOR' ?
      measuresWithClassification.map((attr) =>
        createDataViewAttributeModificationCommand(
          dataViewId,
          attr.id as ActiveAttributeId,
          'TABLE_CELL_PRESENTATION',
          null,
        ),
      )
    : [];
}

function getConditionClone(id: ConditionId) {
  // Cloning, because the condition elements mutate these objects.
  return cloneDeep(getConditionById(getStore().getState(), id));
}

async function createDefaultClassesCombinedClassificationCommands(
  dataViewId: DataViewId,
  command: Command,
  attributeId: ActiveAttributeId | AttributeId,
) {
  const baseAttributeId = getAttributeById(getStore().getState(), attributeId).baseId ?? (attributeId as AttributeId);
  const commands = await createDefaultClassesCommands(dataViewId, baseAttributeId);
  return commands.reduce((acc, curr) => acc.append(curr), command);
}
